import {
  CSSProperties,
  KeyboardEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react"
import { ClockCircleOutlined } from "@ant-design/icons"
import dayjs, { Dayjs } from "dayjs"

import {
  convert12HourTo24Hour,
  display12HourValue,
  getValidMinute,
  nextHourDigits,
  nextMinuteDigits,
  Period,
  resolveHourFromDigits,
  snapMinute,
} from "./timePickerModel"

import "./TimePicker.css"

export type Props = {
  value?: Dayjs
  onChange: (value: Dayjs) => void
  minuteStep?: number
  disabled?: boolean
  style?: CSSProperties
}

const useTwoDigitTimer = () => {
  const [flag, setFlag] = useState(false)
  useEffect(() => {
    if (flag) {
      const timer = setTimeout(() => setFlag(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [flag])
  return [flag, setFlag] as const
}

type DigitInputProps = {
  inputRef: RefObject<HTMLInputElement>
  value: string
  disabled?: boolean
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void
}

const DigitInput = ({ inputRef, value, disabled, onKeyDown }: DigitInputProps) => (
  <input
    ref={inputRef}
    className="time-picker-input"
    type="text"
    inputMode="numeric"
    disabled={disabled}
    value={value}
    onChange={() => undefined}
    onKeyDown={onKeyDown}
  />
)

type HourInputProps = {
  value: Dayjs
  onChange: (value: Dayjs) => void
  period: Period
  disabled?: boolean
  inputRef: RefObject<HTMLInputElement>
  onRightFocus: () => void
}

const HourInput = ({ value, onChange, period, disabled, inputRef, onRightFocus }: HourInputProps) => {
  const [flag, setFlag] = useTwoDigitTimer()
  const [prevIntKey, setPrevIntKey] = useState("0")

  const display = display12HourValue(value.hour())

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") {
      return
    }
    e.preventDefault()
    if (e.key === "ArrowRight") {
      onRightFocus()
      return
    }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      const step = e.key === "ArrowUp" ? 1 : -1
      if (flag) {
        setFlag(false)
      }
      onChange(value.hour((value.hour() + step + 24) % 24))
      return
    }
    if (e.key >= "0" && e.key <= "9") {
      setPrevIntKey(e.key)
      const newValue = nextHourDigits(display, e.key, flag, prevIntKey)
      if (flag) {
        onRightFocus()
      }
      setFlag((prev) => !prev)
      onChange(value.hour(resolveHourFromDigits(newValue, period)))
    }
  }

  return <DigitInput inputRef={inputRef} value={display} disabled={disabled} onKeyDown={handleKeyDown} />
}

type MinuteInputProps = {
  value: Dayjs
  onChange: (value: Dayjs) => void
  step: number
  disabled?: boolean
  inputRef: RefObject<HTMLInputElement>
  onLeftFocus: () => void
  onRightFocus: () => void
}

const MinuteInput = ({
  value,
  onChange,
  step: arrowStep,
  disabled,
  inputRef,
  onLeftFocus,
  onRightFocus,
}: MinuteInputProps) => {
  const [flag, setFlag] = useTwoDigitTimer()

  const display = getValidMinute(String(value.minute()))

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") {
      return
    }
    e.preventDefault()
    if (e.key === "ArrowRight") {
      onRightFocus()
      return
    }
    if (e.key === "ArrowLeft") {
      onLeftFocus()
      return
    }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      const direction = e.key === "ArrowUp" ? 1 : -1
      if (flag) {
        setFlag(false)
      }
      onChange(value.minute(snapMinute(value.minute(), arrowStep, direction)))
      return
    }
    if (e.key >= "0" && e.key <= "9") {
      const newValue = nextMinuteDigits(display, e.key, flag)
      if (flag) {
        onRightFocus()
      }
      setFlag((prev) => !prev)
      onChange(value.minute(parseInt(getValidMinute(newValue), 10)))
    }
  }

  return <DigitInput inputRef={inputRef} value={display} disabled={disabled} onKeyDown={handleKeyDown} />
}

const TimePicker = ({ value, onChange, minuteStep = 5, disabled, style }: Props) => {
  const hourRef = useRef<HTMLInputElement>(null)
  const minuteRef = useRef<HTMLInputElement>(null)
  const periodRef = useRef<HTMLButtonElement>(null)

  const effectiveValue = value ?? dayjs().startOf("day")
  const period: Period = effectiveValue.hour() >= 12 ? "PM" : "AM"

  const setPeriod = (newPeriod: Period) => {
    if (newPeriod === period) {
      return
    }
    const hours12 = parseInt(display12HourValue(effectiveValue.hour()), 10)
    onChange(effectiveValue.hour(convert12HourTo24Hour(hours12, newPeriod)))
  }

  const togglePeriod = () => {
    setPeriod(period === "AM" ? "PM" : "AM")
  }

  const className = `time-picker${disabled ? " time-picker--disabled" : ""}`

  return (
    <div className={className} style={style}>
      <ClockCircleOutlined className="time-picker-icon" />
      <HourInput
        value={effectiveValue}
        onChange={onChange}
        period={period}
        disabled={disabled}
        inputRef={hourRef}
        onRightFocus={() => minuteRef.current?.focus()}
      />
      <span className="time-picker-sep">:</span>
      <MinuteInput
        value={effectiveValue}
        onChange={onChange}
        step={minuteStep}
        disabled={disabled}
        inputRef={minuteRef}
        onLeftFocus={() => hourRef.current?.focus()}
        onRightFocus={() => periodRef.current?.focus()}
      />
      <button
        ref={periodRef}
        type="button"
        className="time-picker-period"
        disabled={disabled}
        onClick={togglePeriod}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") {
            e.preventDefault()
            minuteRef.current?.focus()
            return
          }
          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault()
            togglePeriod()
            return
          }
          const lower = e.key.toLowerCase()
          if (lower === "a") {
            e.preventDefault()
            setPeriod("AM")
            return
          }
          if (lower === "p") {
            e.preventDefault()
            setPeriod("PM")
          }
        }}
      >
        {period}
      </button>
    </div>
  )
}

export default TimePicker
